import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { createServiceManager } from "@/Calls/callDealer";

/* --------------------------------------------
   Schema & Types
-------------------------------------------- */
const ServiceManagerSchema = z.object({
    servicemanager_firstname: z.string().min(2, "First name required"),
    servicemanager_lastname: z.string().min(2, "Last name required"),
    servicemanager_email: z.string().email("Invalid email"),
    servicemanager_password: z.string().min(6, "Password must be at least 6 chars"),
    servicemanager_mobilephone_number: z.string().optional(),
    servicemanager_job_title: z.string().optional(),
});

type ServiceManagerForm = z.infer<typeof ServiceManagerSchema>;

/* --------------------------------------------
   Component
-------------------------------------------- */
export default function ServiceManagerCreatePage() {
    const form = useForm<ServiceManagerForm>({
        resolver: zodResolver(ServiceManagerSchema),
        defaultValues: {
            servicemanager_firstname: "",
            servicemanager_lastname: "",
            servicemanager_email: "",
            servicemanager_password: "",
            servicemanager_mobilephone_number: "",
            servicemanager_job_title: "",
        },
    });

    const [submitting, setSubmitting] = React.useState(false);

    async function onSubmit(values: ServiceManagerForm) {
        try {
            setSubmitting(true);
            await createServiceManager(values);

            toast("✅ Service Manager created", {
                description: `${values.servicemanager_firstname} ${values.servicemanager_lastname} has been added successfully.`,
                action: {
                    label: "View Staff",
                    onClick: () => window.location.assign("/dealer/staff"),
                },
            });

            form.reset();
        } catch (err) {
            console.error(err);
            const message =
                err instanceof Error ? err.message : "Unexpected error — please try again later.";
            toast("❌ Error creating Service Manager", { description: message });
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="container mx-auto max-w-2xl py-8">
            <Card>
                <CardHeader>
                    <CardTitle>Add New Service Manager</CardTitle>
                </CardHeader>
                <CardContent>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="servicemanager_firstname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>First Name</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="John" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="servicemanager_lastname"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Last Name</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Doe" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="servicemanager_email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Email</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="email" placeholder="email@dealer.com" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="servicemanager_password"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Password</FormLabel>
                                        <FormControl>
                                            <Input {...field} type="password" placeholder="********" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="servicemanager_mobilephone_number"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Mobile Phone</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="(555) 555-5555" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="servicemanager_job_title"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Job Title</FormLabel>
                                        <FormControl>
                                            <Input {...field} placeholder="Service Manager" />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button type="submit" disabled={submitting} className="w-full">
                                {submitting ? "Creating..." : "Create Service Manager"}
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>
        </div>
    );
}
